import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadPdf } from "@/lib/pdf";
import { FileText, Plus, X, Save, Trash2, CheckCircle2, XCircle, Send, Search, Download, Wrench, Boxes, Package, FileInput, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { confirmAction } from "@/lib/confirm";
import QuoteItemPicker from "@/components/QuoteItemPicker";
import ExternalQuoteModal from "@/components/ExternalQuoteModal";

const STATUS = {
  brouillon: { label: "Brouillon", cls: "badge-muted" },
  envoye: { label: "Envoyé", cls: "badge-warning" },
  accepte: { label: "Accepté ✓", cls: "badge-success" },
  refuse: { label: "Refusé", cls: "badge-danger" },
  facture: { label: "Facturé", cls: "badge-primary" },
};

const fmtEUR = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

export default function Quotes() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [workItems, setWorkItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    const [a, c, w] = await Promise.all([
      api.get("/quotes"), api.get("/clients"), api.get("/work-items"),
    ]);
    setItems(a.data);
    setClients(c.data);
    setWorkItems(w.data.items);
  };
  useEffect(() => { load(); }, []);

  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
  const filtered = items.filter((it) => !q || `${it.number} ${clientMap[it.client_id]?.last_name || ""}`.toLowerCase().includes(q.toLowerCase()));

  const setStatus = async (id, status) => {
    await api.patch(`/quotes/${id}/status`, { status });
    await load();
    if (status === "accepte") {
      toast.success("Devis accepté — fiches matériel chantier générées automatiquement", { duration: 4000 });
    } else {
      toast.success("Statut mis à jour");
    }
  };

  const remove = async (id) => {
    if (!(await confirmAction("Supprimer ce devis ?"))) return;
    await api.delete(`/quotes/${id}`);
    await load();
  };

  const downloadPdfQuote = (it) => downloadPdf(`/quotes/${it.id}/pdf`, `${it.number || "devis"}.pdf`);

  return (
    <div data-testid="quotes-page" className="max-w-7xl mx-auto">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 font-body text-xs  text-[#FFC400] font-bold mb-3">
            <FileText size={13} strokeWidth={2.5} /> Module 4 — Devis
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[var(--chalk)]">Devis</h1>
          <p className="text-[var(--ink-muted)] mt-3 font-body">Statut <b>Accepté</b> → fiches matériel auto-générées · Bouton <b>PDF</b> pour télécharger.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--grey)]" />
            <input className="input pl-9 w-64" placeholder="N° ou client..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="flex flex-col gap-2">
            <button onClick={() => setModal("new")} className="btn btn-accent" data-testid="add-quote">
              <Plus size={15} /> Nouveau devis
            </button>
            <button
              onClick={() => setModal("external")}
              className="btn btn-outline"
              style={{ minHeight: 36, fontSize: "0.78rem" }}
              data-testid="add-external-quote"
              title="Pour les devis signés hors PitBoard (Henry, Excel, papier…) — comptabilisés dans vos indicateurs de pilotage."
            >
              <FileInput size={13} /> Devis externe
            </button>
          </div>
        </div>
      </header>

      <div className="card overflow-hidden p-0">
        {filtered.length === 0 ? (
          <div className="p-16 text-center text-[var(--grey)]">
            <FileText size={36} className="mx-auto mb-3 opacity-40" strokeWidth={2} />
            <div className="font-display text-[var(--chalk)] mb-1">Aucun devis</div>
          </div>
        ) : (
          <table className="table">
            <thead><tr>
              <th>N°</th><th>Client</th><th>Projet</th><th>Statut</th><th className="text-right">Total HT</th><th className="text-right">Total TTC</th><th className="w-80">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map((it) => {
                const c = clientMap[it.client_id];
                const st = STATUS[it.status] || STATUS.brouillon;
                return (
                  <tr key={it.id} data-testid={`quote-${it.id}`}>
                    <td className="font-mono text-xs font-bold">{it.number}</td>
                    <td>{c ? `${c.last_name} ${c.first_name || ""}` : "—"}</td>
                    <td className="text-sm text-[var(--ink-muted)]">{it.project_name || "—"}</td>
                    <td><span className={`badge ${st.cls}`}>{st.label}</span></td>
                    <td className="text-right font-mono tabular-nums">{fmtEUR(it.total_ht)}</td>
                    <td className="text-right font-mono tabular-nums font-bold">{fmtEUR(it.total_ttc)}</td>
                    <td>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {it.status === "brouillon" && <button onClick={() => setStatus(it.id, "envoye")} className="btn btn-sm btn-outline" data-testid={`qs-send-${it.id}`}><Send size={12} /> Envoyer</button>}
                        {(it.status === "envoye" || it.status === "brouillon") && (
                          <>
                            <button onClick={() => setStatus(it.id, "accepte")} className="btn btn-sm btn-accent" data-testid={`qs-accept-${it.id}`}><CheckCircle2 size={12} /> Accepté</button>
                            <button onClick={() => setStatus(it.id, "refuse")} className="btn btn-sm bg-red-600 text-white hover:bg-red-700 border-2 border-zinc-900" data-testid={`qs-reject-${it.id}`}><XCircle size={12} /> Refusé</button>
                          </>
                        )}
                        <button onClick={() => downloadPdfQuote(it)} className="btn btn-sm btn-outline" title="Télécharger PDF" data-testid={`q-pdf-${it.id}`}><Download size={12} /> PDF</button>
                        <button onClick={() => setModal(it)} className="btn btn-ghost btn-sm" title="Modifier"><FileText size={12} /></button>
                        <button onClick={() => remove(it.id)} className="w-7 h-7 rounded border border-[var(--steel)] text-[#E10600] hover:bg-[rgba(225,6,0,0.12)] hover:border-[#E10600] inline-flex items-center justify-center transition-colors" data-testid={`q-delete-${it.id}`}><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {modal && modal !== "external" && <QuoteModal item={modal === "new" ? null : modal} clients={clients} workItems={workItems}
        onClose={() => setModal(null)} onSaved={async () => { await load(); setModal(null); }} />}
      {modal === "external" && <ExternalQuoteModal
        onClose={() => setModal(null)}
        onSaved={async () => { await load(); setModal(null); }}
      />}
    </div>
  );
}

function QuoteModal({ item, clients, workItems, onClose, onSaved }) {
  const blank = { client_id: clients[0]?.id || "", project_name: "", notes: "", lines: [], vat_rate: 20, validity_days: 30,
    distance_km: 0, trips_count: 0, fuel_price_per_l: 1.85, consumption_l_per_100km: 9, cgv_text: "" };
  const [form, setForm] = useState(item || blank);
  const [pickerTab, setPickerTab] = useState(null); // null | "ouvrage" | "article" | "materiau"
  const [saving, setSaving] = useState(false);

  const addLineFromPicker = (line) => {
    setForm((f) => ({ ...f, lines: [...f.lines, line] }));
    setPickerTab(null);
    toast.success("Ligne ajoutée");
  };
  const updateLine = (i, patch) => {
    const c = [...form.lines]; c[i] = { ...c[i], ...patch }; setForm({ ...form, lines: c });
  };
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) });

  const totalHT = form.lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price_ht) || 0), 0);
  // Calcul TVA par ligne (fallback sur vat_rate global du devis)
  const vatBreakdown = {};
  let vat = 0;
  for (const l of form.lines) {
    const ht = (Number(l.quantity) || 0) * (Number(l.unit_price_ht) || 0);
    const rate = l.vat_rate != null && l.vat_rate !== "" ? Number(l.vat_rate) : Number(form.vat_rate);
    const v = ht * rate / 100;
    vat += v;
    const k = rate.toFixed(1);
    if (!vatBreakdown[k]) vatBreakdown[k] = { rate, base: 0, vat: 0 };
    vatBreakdown[k].base += ht;
    vatBreakdown[k].vat += v;
  }
  const ttc = totalHT + vat;
  const multipleVat = Object.keys(vatBreakdown).length > 1;

  const save = async () => {
    if (!form.client_id) { toast.error("Client requis"); return; }
    setSaving(true);
    try {
      const payload = { ...form,
        distance_km: Number(form.distance_km) || 0,
        trips_count: Number(form.trips_count) || 0,
        fuel_price_per_l: Number(form.fuel_price_per_l) || 1.85,
        consumption_l_per_100km: Number(form.consumption_l_per_100km) || 9,
        lines: form.lines.map((l) => ({
          ...l,
          quantity: Number(l.quantity),
          unit_price_ht: Number(l.unit_price_ht),
          vat_rate: l.vat_rate != null && l.vat_rate !== "" ? Number(l.vat_rate) : null,
        })) };
      if (item) await api.patch(`/quotes/${item.id}`, payload);
      else await api.post("/quotes", payload);
      toast.success(item ? "Devis mis à jour" : "Devis créé");
      await onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="card max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="quote-modal">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-lg">{item ? `Devis ${item.number}` : "Nouveau devis"}</h3>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={14} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Client *</label>
              <select className="input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                <option value="">— Choisir —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.last_name} {c.first_name} {c.company_name ? `(${c.company_name})` : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Nom du chantier</label>
              <input className="input" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} placeholder="Ex: Appartement Martin" />
            </div>
          </div>

          {/* Coût carburant caché */}
          <details className="card p-3 bg-[rgba(255,196,0,0.06)] border-[rgba(255,196,0,0.25)]">
            <summary className="cursor-pointer text-sm font-medium text-[#FFC400]">🚗 Coût carburant (caché — non visible client)</summary>
            <div className="grid grid-cols-4 gap-3 mt-3">
              <div><label className="label">Distance A/R (km)</label><input type="number" step="0.1" className="input font-mono" value={form.distance_km || ""} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} placeholder="Ex: 45" /></div>
              <div><label className="label">Nb de trajets</label><input type="number" className="input font-mono" value={form.trips_count || ""} onChange={(e) => setForm({ ...form, trips_count: e.target.value })} placeholder="Ex: 8" /></div>
              <div><label className="label">Conso L/100km</label><input type="number" step="0.1" className="input font-mono" value={form.consumption_l_per_100km || 9} onChange={(e) => setForm({ ...form, consumption_l_per_100km: e.target.value })} /></div>
              <div><label className="label">Prix L (€)</label><input type="number" step="0.01" className="input font-mono" value={form.fuel_price_per_l || 1.85} onChange={(e) => setForm({ ...form, fuel_price_per_l: e.target.value })} /></div>
            </div>
            <div className="mt-3 text-xs text-[#FFCF40] font-mono">
              Total km : {((Number(form.distance_km) || 0) * (Number(form.trips_count) || 0)).toFixed(0)} km · Coût caché : <b>{fmtEUR((Number(form.distance_km) || 0) * (Number(form.trips_count) || 0) * ((Number(form.consumption_l_per_100km) || 9) / 100) * (Number(form.fuel_price_per_l) || 1.85))}</b>
            </div>
          </details>

          <div className="card p-4 bg-[var(--carbon-3)]">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div>
                <div className="label" style={{ marginBottom: 2 }}>Ajouter une ligne au devis</div>
                <div className="text-xs" style={{ color: "var(--grey)" }}>
                  Choisissez la source — recherche, filtre par catégorie et double-clic dans le tableau.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setPickerTab("ouvrage")}
                className="btn btn-accent"
                data-testid="add-line-ouvrage"
              >
                <Wrench size={14} /> Main d&apos;œuvre (Ouvrages)
              </button>
              <button
                type="button"
                onClick={() => setPickerTab("article")}
                className="btn btn-outline"
                data-testid="add-line-article"
              >
                <Boxes size={14} /> Articles
              </button>
              <button
                type="button"
                onClick={() => setPickerTab("materiau")}
                className="btn btn-outline"
                data-testid="add-line-materiau"
              >
                <Package size={14} /> Matériaux
              </button>
            </div>
          </div>

          {form.lines.length > 0 && (
            <div className="overflow-x-auto">
              <table className="table">
                <thead><tr><th>Description</th><th className="text-right w-24">Qté</th><th className="w-20">Unité</th><th className="text-right w-32">PU HT</th><th className="text-right w-20">TVA</th><th className="text-right">Total HT</th><th></th></tr></thead>
                <tbody>
                  {form.lines.map((l, i) => {
                    const wi = workItems.find((w) => w.id === l.work_item_id);
                    const isArt = wi?.kind === "article";
                    const stock = Number(wi?.stock_qty || 0);
                    const qty = Number(l.quantity || 0);
                    const shortStock = isArt && qty > stock;
                    // Alerte marge négative : compare PU HT vendu vs coût unitaire calculé
                    const unitCost = Number(wi?.unit_cost_ht || 0);
                    const unitPrice = Number(l.unit_price_ht || 0);
                    const negMargin = wi && unitCost > 0 && unitPrice > 0 && unitPrice < unitCost;
                    return (
                      <tr key={i}>
                        <td className="text-sm">
                          {l.code && <span className="font-mono text-xs text-tertiary-text">{l.code}</span>}
                          <div className="flex items-center gap-2 flex-wrap">
                            {isArt && <span className="badge badge-accent" style={{ fontSize: "0.65rem", padding: "0.1rem 0.45rem" }}>Article</span>}
                            <span>{l.description}</span>
                          </div>
                          {isArt && (
                            <div className="text-[11px] mt-0.5" style={{ color: shortStock ? "#E10600" : "var(--grey)" }}>
                              Stock actuel : <b>{stock}</b> {wi?.unit || ""}
                              {shortStock && " — rupture après facturation !"}
                            </div>
                          )}
                          {negMargin && (
                            <div
                              className="mt-1 inline-flex items-center gap-1.5 px-2 py-1 rounded"
                              style={{
                                background: "rgba(225,6,0,0.15)",
                                color: "#E10600",
                                border: "1px solid rgba(225,6,0,0.4)",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                              }}
                              title={`Coût unitaire estimé : ${fmtEUR(unitCost)} · Vous vendez sous le seuil de rentabilité.`}
                              data-testid={`line-margin-alert-${i}`}
                            >
                              <AlertTriangle size={13} strokeWidth={2.4} />
                              <span>Marge négative</span>
                            </div>
                          )}
                        </td>
                        <td><input type="number" step="0.1" className="input text-right font-mono" value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} data-testid={`line-qty-${i}`} /></td>
                        <td className="text-sm">{l.unit}</td>
                        <td><input type="number" step="0.01" className="input text-right font-mono" value={l.unit_price_ht} onChange={(e) => updateLine(i, { unit_price_ht: e.target.value })} data-testid={`line-pu-${i}`} /></td>
                        <td>
                          <select
                            className="input text-right font-mono"
                            value={l.vat_rate ?? ""}
                            onChange={(e) => updateLine(i, { vat_rate: e.target.value === "" ? null : e.target.value })}
                            title="TVA spécifique à cette ligne. Vide = TVA par défaut du devis."
                            data-testid={`line-vat-${i}`}
                          >
                            <option value="">Défaut ({form.vat_rate}%)</option>
                            <option value="0">0%</option>
                            <option value="5.5">5,5%</option>
                            <option value="10">10%</option>
                            <option value="20">20%</option>
                          </select>
                        </td>
                        <td className="text-right font-mono font-semibold">{fmtEUR((Number(l.quantity) || 0) * (Number(l.unit_price_ht) || 0))}</td>
                        <td><button onClick={() => removeLine(i)} className="text-[#E10600] hover:bg-[rgba(225,6,0,0.12)] w-7 h-7 rounded inline-flex items-center justify-center"><Trash2 size={12} /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="bg-[var(--carbon-3)] text-white p-4 rounded-lg space-y-2 text-sm">
            <div className="flex justify-between"><span>Total HT</span><span className="font-mono">{fmtEUR(totalHT)}</span></div>
            {multipleVat ? (
              <>
                {Object.values(vatBreakdown).sort((a, b) => b.rate - a.rate).map((b) => (
                  <div key={b.rate} className="flex justify-between text-[var(--ink-soft)] text-[13px]" data-testid={`vat-bucket-${b.rate}`}>
                    <span>TVA {b.rate}% sur {fmtEUR(b.base)}</span>
                    <span className="font-mono">{fmtEUR(b.vat)}</span>
                  </div>
                ))}
              </>
            ) : (
              <div className="flex justify-between text-[var(--ink-soft)]"><span>TVA {form.vat_rate}%</span><span className="font-mono">{fmtEUR(vat)}</span></div>
            )}
            <div className="flex justify-between text-lg pt-2 border-t border-white/20 font-semibold"><span>Total TTC</span><span className="font-mono">{fmtEUR(ttc)}</span></div>
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2 sticky bottom-0 bg-[var(--surface)]">
          <button onClick={onClose} className="btn btn-outline">Annuler</button>
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="quote-save"><Save size={14} /> {saving ? "..." : "Enregistrer"}</button>
        </div>
      </div>

      {pickerTab && (
        <QuoteItemPicker
          workItems={workItems}
          defaultTab={pickerTab}
          onPick={addLineFromPicker}
          onClose={() => setPickerTab(null)}
        />
      )}
    </div>
  );
}
